import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Link as LinkIcon,
  File,
  Trash,
  ExternalLink,
  Download,
  Edit,
  CheckCircle,
  AlertCircle,
  Clock,
  MoreHorizontal,
  MessageCircle,
  Phone,
} from "lucide-react";
import { KnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { formatDistanceToNow } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import KnowledgeItemDetailDialog from "./KnowledgeItemDetailDialog";
import EditLinkDialog from "./EditLinkDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface KnowledgeItemCardProps {
  item: KnowledgeItem;
  onDelete: () => Promise<boolean>;
  onEdit?: () => void;
}

const KnowledgeItemCard = ({
  item,
  onDelete,
  onEdit,
}: KnowledgeItemCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditLinkOpen, setIsEditLinkOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { toast } = useToast();
  const { organization } = useAuth();
  const hideChatAgents = organization?.hide_chat_agents ?? true;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown date";
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const getFileSize = (sizeInBytes: number | null) => {
    if (sizeInBytes === null) return "Unknown size";
    const kb = sizeInBytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const success = await onDelete();
    setIsDeleting(false);
    setIsDeleteDialogOpen(false);
  };

  const downloadFile = async () => {
    if (item.type !== "file" || !item.file_path) return;

    setIsDownloading(true);
    try {
      const { data, error } = await supabase.storage
        .from("knowledge_files")
        .download(item.file_path);

      if (error) {
        throw error;
      }

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.file_name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: `Downloading ${item.file_name}`,
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "The file could not be downloaded.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const renderIcon = () => {
    switch (item.type) {
      case "snippet":
        return <FileText className="h-5 w-5 text-green-500 flex-shrink-0" />;
      case "file":
        return <File className="h-5 w-5 text-blue-500 flex-shrink-0" />;
      case "link":
        return <LinkIcon className="h-5 w-5 text-purple-500 flex-shrink-0" />;
      default:
        return <FileText className="h-5 w-5 flex-shrink-0" />;
    }
  };

  const handleCardClick = () => {
    if (item.type === "snippet") {
      setIsDetailOpen(true);
    } else if (item.type === "link") {
      setIsEditLinkOpen(true);
    }
  };

  const getSubtitle = () => {
    switch (item.type) {
      case "file":
        return `${item.file_type || "File"} • ${getFileSize(item.file_size)}`;
      case "link":
        return item.url || "No URL";
      case "snippet":
        const content = item.content || item.description || "";
        return content.length > 100
          ? content.substring(0, 100) + "..."
          : content;
      default:
        return "";
    }
  };

  const getAIStatus = () => {
    if (item.ai_processed) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                <MessageCircle className="h-3 w-3 text-green-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Ready to use by your AI assistants</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else if (item.ai_processing_error) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 bg-red-100 rounded-full">
                <AlertCircle className="h-3 w-3 text-red-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Processing error - please try again</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 bg-yellow-100 rounded-full">
                <Clock className="h-3 w-3 text-yellow-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Processing for AI assistants...</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  };

  const getVapiStatus = () => {
    if (item.vapi_file_id && item.vapi_upload_status === "done") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                <Phone className="h-3 w-3 text-green-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Ready to use by your phone agents</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else if (item.vapi_upload_status === "failed") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 bg-red-100 rounded-full">
                <AlertCircle className="h-3 w-3 text-red-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Voice upload failed - will retry on next update</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    } else if (item.vapi_upload_status === "processing") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-6 h-6 bg-yellow-100 rounded-full">
                <Clock className="h-3 w-3 text-yellow-600" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Uploading to voice agents...</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return null;
  };

  return (
    <>
      <div
        className={cn(
          "flex items-center justify-between p-4 border rounded-lg transition-colors min-h-[72px]",
          item.type === "link" || item.type === "snippet"
            ? "hover:bg-accent/50 cursor-pointer"
            : "hover:bg-accent/50",
        )}
        onClick={
          item.type === "link" || item.type === "snippet"
            ? handleCardClick
            : undefined
        }
      >
        {/* Left section - Icon and content */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {renderIcon()}

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium truncate leading-5">
              {item.title}
            </h3>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
              {getSubtitle()}
            </p>
          </div>
        </div>

        {/* Right section - Status, date, and menu */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* AI Status - hide if chat agents are disabled */}
          {!hideChatAgents && getAIStatus()}

          {/* Voice Agent Status */}
          {getVapiStatus()}

          {/* Date */}
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatDate(item.created_at)}
          </span>

          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>

              {/* View/Edit actions */}
              {item.type === "snippet" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDetailOpen(true);
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  View/Edit
                </DropdownMenuItem>
              )}

              {item.type === "link" && (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditLinkOpen(true);
                    }}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Link
                  </DropdownMenuItem>
                  {item.url && (
                    <DropdownMenuItem asChild>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Visit Link
                      </a>
                    </DropdownMenuItem>
                  )}
                </>
              )}

              {item.type === "file" && (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDetailOpen(true);
                    }}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadFile();
                    }}
                    disabled={isDownloading}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteDialogOpen(true);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Dialogs */}
      <KnowledgeItemDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        item={item}
      />

      <EditLinkDialog
        open={isEditLinkOpen}
        onOpenChange={setIsEditLinkOpen}
        item={item}
        onSuccess={() => {
          setIsEditLinkOpen(false);
          onEdit?.();
        }}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              knowledge item "{item.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <span className="h-4 w-4 mr-1 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default KnowledgeItemCard;
